import { Button, Tooltip } from '@ibsys/kit';
import { ArrowDownIcon, ArrowUpIcon, XIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FiltersDescriptionFilter } from './FiltersDescriptionFilter';
import { FiltersDescriptionValues } from './FiltersDescriptionValues';
import {
  type DescriptionActions,
  type DescriptionPart,
  DESCRIPTION_SPACE,
  joinValuesText,
  VALUE_SEPARATOR,
} from './model/filtersDescription';

/** Пропсы части описания отбора */
type Props = {
  part: DescriptionPart;
  /** Сколько значений части свёрнуто в счётчик за первым показанным */
  hiddenCount: number;
  /** Предел длины значений части в знаках; `null` — предела нет, они показаны целиком */
  valueLimit: number | null;
  /** Управление отбором из описания */
  actions: DescriptionActions;
};

const DIRECTION_ICONS = { asc: ArrowUpIcon, desc: ArrowDownIcon };

/** Общий вид кнопок управления в строке описания: при нехватке ширины сокращается текст, а не они */
const CONTROL_CLASS = 'ml-0.5 size-5 shrink-0';

/** Сколько значений подсказка перечисляет поимённо: дальше она сама становится нечитаемой */
const TOOLTIP_VALUES_LIMIT = 10;

/**
 * Часть описания отбора: подпись колонки, её значения и управление ими. Часть не сжимается: её
 * ширина складывается из содержимого, поэтому содержимое не выходит за её границы и не рисуется
 * поверх соседней части, а кнопки управления не срезаются её краем.
 */
export const FiltersDescriptionPart: FC<Props> = ({ part, hiddenCount, valueLimit, actions }) => {
  const { t } = useTranslation(['dialogs']);

  const visibleValues = part.values.slice(0, part.values.length - hiddenCount);
  const valuesText = joinValuesText(visibleValues);

  // У сортировки управление стоит у каждой названной колонки, а не у части целиком
  if (part.kind === 'sorting') {
    return (
      <span className="flex shrink-0 items-center">
        {visibleValues.map((value, index) => {
          const { sorting } = value;
          if (!sorting) return null;
          const DirectionIcon = DIRECTION_ICONS[sorting.direction];
          const toggleDirectionLabel = t('dialogs:dialogListSection.filtersDescription.controls.toggleSortDirection', {
            column: value.text,
            direction: t(`dialogs:dialogListSection.filtersDescription.sortDirection.${sorting.direction}`),
          });
          const clearSortingLabel = t('dialogs:dialogListSection.filtersDescription.controls.clearSorting', {
            column: value.text,
          });
          return (
            <span
              key={`${value.text}-${index}`}
              data-sort-direction={sorting.direction}
              className="flex shrink-0 items-center"
            >
              <span className="shrink-0">{value.text}</span>
              <Button
                variant="ghost"
                size="icon"
                className={CONTROL_CLASS}
                aria-label={toggleDirectionLabel}
                tooltip={toggleDirectionLabel}
                icon={<DirectionIcon className="size-3.5" />}
                onClick={() => actions.onToggleSortDirection(sorting.columnId)}
              />
              {/* У сортировки по умолчанию снятия нет — снимать нечего */}
              {sorting.isDefault ? null : (
                <Button
                  variant="ghost"
                  size="icon"
                  className={CONTROL_CLASS}
                  aria-label={clearSortingLabel}
                  tooltip={clearSortingLabel}
                  icon={<XIcon className="size-3.5" />}
                  onClick={() => actions.onClearSorting(sorting.columnId)}
                />
              )}
              {index < visibleValues.length - 1 ? (
                <span className="shrink-0">
                  {VALUE_SEPARATOR}
                  {DESCRIPTION_SPACE}
                </span>
              ) : null}
            </span>
          );
        })}
      </span>
    );
  }

  const hasHead = Boolean(part.label || part.connector);
  const removeFilterLabel = part.label
    ? t('dialogs:dialogListSection.filtersDescription.controls.removeFilter', { column: part.label })
    : t('dialogs:dialogListSection.filtersDescription.controls.removeUnnamedFilter');
  const removeSelectionLabel = t('dialogs:dialogListSection.deselectSelection.label');

  // В подсказке отбор части назван полностью — тем, что не поместилось в строку и ушло в счётчик
  const restCount = part.values.length - TOOLTIP_VALUES_LIMIT;
  const listedValues = part.values
    .slice(0, TOOLTIP_VALUES_LIMIT)
    .map((value) => value.text)
    .join(`${VALUE_SEPARATOR} `);
  const valuesTooltip =
    restCount > 0
      ? `${listedValues} ${t('dialogs:dialogListSection.filtersDescription.foldedCount', { count: restCount })}`
      : listedValues;

  return (
    <span className="flex shrink-0 items-center">
      {/* Подсказка накрывает название колонки и значения; у кнопок управления свои подсказки */}
      <Tooltip tooltip={valuesTooltip}>
        <span className="flex shrink-0 items-center">
          {part.label ? <span className="shrink-0">{part.label}</span> : null}
          {part.connector ? (
            <>
              <span className="shrink-0">{DESCRIPTION_SPACE}</span>
              <span className="shrink-0">{part.connector}</span>
            </>
          ) : null}
          {hasHead ? <span className="shrink-0">{DESCRIPTION_SPACE}</span> : null}
          {part.kind === 'filter' && part.editor ? (
            <FiltersDescriptionFilter
              columnId={part.id}
              editor={part.editor}
              text={valuesText}
              foldedCount={hiddenCount}
              limitChars={valueLimit}
              onChange={(value) => actions.onChangeFilter(part.id, value)}
            />
          ) : (
            <span className="flex shrink-0 items-center">
              <FiltersDescriptionValues text={valuesText} foldedCount={hiddenCount} limitChars={valueLimit} />
            </span>
          )}
        </span>
      </Tooltip>
      {part.kind === 'filter' ? (
        <Button
          variant="ghost"
          size="icon"
          className={CONTROL_CLASS}
          aria-label={removeFilterLabel}
          tooltip={removeFilterLabel}
          icon={<XIcon className="size-3.5" />}
          onClick={() => actions.onRemoveFilter(part.id)}
        />
      ) : null}
      {/* Выборка перехода снимается целиком — поля в панели фильтров у неё нет */}
      {part.kind === 'selection' && actions.onRemoveSelection ? (
        <Button
          variant="ghost"
          size="icon"
          className={CONTROL_CLASS}
          aria-label={removeSelectionLabel}
          tooltip={removeSelectionLabel}
          icon={<XIcon className="size-3.5" />}
          onClick={actions.onRemoveSelection}
        />
      ) : null}
    </span>
  );
};
